'use client';

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useChat } from '@/lib/hooks/useChat';
import toast from 'react-hot-toast';
import Link from 'next/link';

// ─── TYPES ────────────────────────────────────────────────────────────────────

interface FissionResult {
  layer: string;
  arabic: string;
  content: string;
  mrgLink?: string;
  namesLink?: string;
}

interface ObservationRecord {
  id: string;
  imagePreview: string;
  timestamp: string;
  fissionLayers: FissionResult[];
  mrgContext: string;
  submitted: boolean;
}

// ─── IRL LENS FISSION LAYERS ──────────────────────────────────────────────────

const IRL_LAYERS = [
  { label: 'Zahir', arabic: 'الظَّاهِر', prompt: 'Describe what is outwardly visible in this image — the surface reality, what the senses perceive.' },
  { label: 'Sunnat Allah', arabic: 'سُنَّة اللَّه', prompt: 'Identify the patterns and laws of Allah visible in this image — the systems, cycles, and design principles at work.' },
  { label: 'Haqq', arabic: 'الْحَقّ', prompt: 'What deeper truth does this image reveal? What is the reality beneath the surface?' },
  { label: 'Amanah', arabic: 'الأَمَانَة', prompt: 'What trust or responsibility does this image call the observer to? What Amanah is revealed?' },
  { label: 'Uboor', arabic: 'عُبُور', prompt: 'How does this recognition cross into action? What bridge does this image create between inner recognition and outer service?' },
];

const MRG_CONTEXT_PROMPTS = [
  'What community resources or disparities does this image relate to?',
  'How does what is shown in this image connect to the Muslim Resource Guide — what needs, resources, or community context does it reveal?',
];

// ─── CAMERA GATEWAY ───────────────────────────────────────────────────────────

export default function CameraGateway() {
  const [mode, setMode] = useState<'idle' | 'camera' | 'upload' | 'analyzing' | 'results'>('idle');
  const [imageData, setImageData] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [currentLayer, setCurrentLayer] = useState(0);
  const [fissionResults, setFissionResults] = useState<FissionResult[]>([]);
  const [mrgContext, setMrgContext] = useState('');
  const [observations, setObservations] = useState<ObservationRecord[]>([]);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [streamActive, setStreamActive] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const { response, isLoading, error, sendMessage } = useChat('ANTHROPIC', 'claude-sonnet-4-5-20250929', false);

  useEffect(() => {
    if (error) toast.error(error.message);
  }, [error]);

  // Start camera
  const startCamera = useCallback(async () => {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
        setStreamActive(true);
        setMode('camera');
      }
    } catch (err: any) {
      setCameraError(err.message || 'Camera access denied. Please allow camera access to use live fission sequences.');
    }
  }, []);

  // Stop camera
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setStreamActive(false);
  }, []);

  // Capture from camera
  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const video = videoRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
    const base64 = dataUrl.split(',')[1];
    setImageData(base64);
    setImagePreview(dataUrl);
    stopCamera();
    setMode('analyzing');
    runFission(base64);
  }, [stopCamera]);

  // Handle file upload
  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result as string;
      const base64 = dataUrl.split(',')[1];
      setImageData(base64);
      setImagePreview(dataUrl);
      setMode('analyzing');
      runFission(base64);
    };
    reader.readAsDataURL(file);
  }, []);

  // Run IRL Lens fission on image
  const runFission = useCallback(async (base64: string) => {
    setFissionResults([]);
    setCurrentLayer(0);
    const results: FissionResult[] = [];

    for (let i = 0; i < IRL_LAYERS.length; i++) {
      setCurrentLayer(i);
      const layer = IRL_LAYERS[i];
      const systemPrompt = `You are the IRL Lens — the Inner Reality Lens framework from The Haal Project (haalproject.org). You analyze images through the lens of Islamic spiritual intelligence, revealing the layers of reality embedded in what is seen. Be concise, insightful, and grounded in the Islamic intellectual tradition. Respond in 2-3 sentences.`;
      const userPrompt = `${layer.prompt}\n\nApply this to the image provided. Connect your observation to the Names of Allah and Sunnat Allah (patterns in creation) where relevant.`;

      await sendMessage([
        { role: 'system', content: systemPrompt },
        { role: 'user', content: [{ type: 'text', text: userPrompt }, { type: 'image_url', image_url: { url: `data:image/jpeg;base64,${base64}` } }] as any },
      ], { max_tokens: 200, temperature: 0.7 });

      // Wait for response (non-streaming)
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // Get MRG context
    await sendMessage([
      { role: 'system', content: 'You are the Muslim Resource Guide lens from The Haal Project. Analyze images for community resource and disparity connections. Be brief and practical — 2 sentences.' },
      { role: 'user', content: [{ type: 'text', text: MRG_CONTEXT_PROMPTS[0] }, { type: 'image_url', image_url: { url: `data:image/jpeg;base64,${base64}` } }] as any },
    ], { max_tokens: 150, temperature: 0.5 });

    setMode('results');
  }, [sendMessage]);

  // Since useChat is non-streaming and resets on each call, we build results from response
  useEffect(() => {
    if (response && mode === 'analyzing' && !isLoading) {
      const layer = IRL_LAYERS[currentLayer];
      if (layer && fissionResults.length <= currentLayer) {
        setFissionResults(prev => [...prev, {
          layer: layer.label,
          arabic: layer.arabic,
          content: response,
          mrgLink: currentLayer === 4 ? 'Muslim Resource Guide' : undefined,
          namesLink: currentLayer === 1 ? 'Names of Allah' : undefined,
        }]);
      }
    }
  }, [response, isLoading]);

  // Save observation
  const saveObservation = () => {
    if (!imagePreview || fissionResults.length === 0) return;
    const record: ObservationRecord = {
      id: Date.now().toString(),
      imagePreview,
      timestamp: new Date().toISOString(),
      fissionLayers: fissionResults,
      mrgContext,
      submitted: false,
    };
    setObservations(prev => [record, ...prev]);
    toast.success('Observation saved to your chain');
  };

  const reset = () => {
    setMode('idle');
    setImageData(null);
    setImagePreview(null);
    setFissionResults([]);
    setMrgContext('');
    setCurrentLayer(0);
    stopCamera();
  };

  return (
    <div style={{ background: 'rgba(6,13,18,0.95)', border: '1px solid rgba(45,138,135,0.25)', borderRadius: 12, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '14px 18px', borderBottom: '1px solid rgba(45,138,135,0.15)', background: 'rgba(45,138,135,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <p style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.15em', textTransform: 'uppercase', color: 'rgba(45,138,135,0.6)', marginBottom: 2 }}>📷 IRL Lens · Camera Gateway</p>
          <h3 style={{ fontFamily: 'monospace', fontSize: 13, color: '#F5F0E8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em' }}>Live Fission Sequences</h3>
        </div>
        {mode !== 'idle' && (
          <button onClick={reset} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, padding: '5px 10px', color: 'rgba(212,197,169,0.4)', fontFamily: 'monospace', fontSize: 8, cursor: 'pointer', letterSpacing: '.06em', textTransform: 'uppercase' }}>
            ← Reset
          </button>
        )}
      </div>

      <div style={{ padding: '18px' }}>
        {/* IDLE STATE */}
        {mode === 'idle' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 12, lineHeight: 1.7 }}>
              Attach your camera to run the IRL Lens on real pictures — the framework unfolding on what you see, directly linked to the Muslim Resource Guide and SL concepts. Each photo becomes an observation in your chain.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <button onClick={startCamera} style={{ padding: '14px 16px', background: 'rgba(45,138,135,0.1)', border: '1px solid rgba(45,138,135,0.35)', borderRadius: 8, color: '#2d8a87', fontFamily: 'monospace', fontSize: 10, letterSpacing: '.08em', textTransform: 'uppercase', cursor: 'pointer', transition: 'all 0.2s', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 24 }}>📷</span>
                Live Camera
              </button>
              <button onClick={() => fileInputRef.current?.click()} style={{ padding: '14px 16px', background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: 8, color: '#c9a84c', fontFamily: 'monospace', fontSize: 10, letterSpacing: '.08em', textTransform: 'uppercase', cursor: 'pointer', transition: 'all 0.2s', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 24 }}>🖼️</span>
                Upload Photo
              </button>
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileUpload} style={{ display: 'none' }} />
            {cameraError && (
              <div style={{ padding: '10px 14px', background: 'rgba(180,80,80,0.08)', border: '1px solid rgba(180,80,80,0.25)', borderRadius: 6 }}>
                <p style={{ color: '#e05a5a', fontSize: 11, lineHeight: 1.6 }}>{cameraError}</p>
              </div>
            )}
            <div style={{ padding: '12px 14px', background: 'rgba(255,255,255,0.01)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 8 }}>
              <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.3)', letterSpacing: '.08em', lineHeight: 1.7 }}>
                Powered by Anthropic Vision (Claude) · IRL Lens Framework · Linked to MRG + SL Concepts
              </p>
            </div>
          </div>
        )}

        {/* CAMERA STATE */}
        {mode === 'camera' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', background: '#000', aspectRatio: '4/3' }}>
              <video ref={videoRef} autoPlay playsInline muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              <div style={{ position: 'absolute', inset: 0, border: '2px solid rgba(45,138,135,0.4)', borderRadius: 8, pointerEvents: 'none' }}>
                <div style={{ position: 'absolute', top: 8, left: 8, fontFamily: 'monospace', fontSize: 8, color: 'rgba(45,138,135,0.8)', letterSpacing: '.1em', textTransform: 'uppercase', background: 'rgba(6,13,18,0.7)', padding: '3px 8px', borderRadius: 4 }}>
                  ◎ IRL LENS ACTIVE
                </div>
              </div>
            </div>
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            <button onClick={capturePhoto} style={{ padding: '12px', background: 'rgba(45,138,135,0.15)', border: '2px solid rgba(45,138,135,0.5)', borderRadius: 8, color: '#2d8a87', fontFamily: 'monospace', fontSize: 11, letterSpacing: '.1em', textTransform: 'uppercase', cursor: 'pointer', transition: 'all 0.2s' }}>
              📷 Capture & Run Fission
            </button>
          </div>
        )}

        {/* ANALYZING STATE */}
        {mode === 'analyzing' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {imagePreview && (
              <div style={{ borderRadius: 8, overflow: 'hidden', maxHeight: 200 }}>
                <img src={imagePreview} alt="Captured for IRL Lens fission analysis" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
            )}
            <div style={{ padding: '14px 16px', background: 'rgba(45,138,135,0.06)', border: '1px solid rgba(45,138,135,0.2)', borderRadius: 8 }}>
              <p style={{ fontFamily: 'monospace', fontSize: 9, color: '#2d8a87', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 8 }}>
                ◎ Running IRL Lens Fission...
              </p>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {IRL_LAYERS.map((layer, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', background: i <= currentLayer ? 'rgba(45,138,135,0.15)' : 'rgba(255,255,255,0.03)', border: `1px solid ${i <= currentLayer ? 'rgba(45,138,135,0.4)' : 'rgba(255,255,255,0.06)'}`, borderRadius: 20 }}>
                    {i < currentLayer ? (
                      <span style={{ color: '#2d7a47', fontSize: 8 }}>✓</span>
                    ) : i === currentLayer && isLoading ? (
                      <span style={{ color: '#2d8a87', fontSize: 8, animation: 'pulse 1s infinite' }}>◎</span>
                    ) : (
                      <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: 8 }}>○</span>
                    )}
                    <span style={{ fontFamily: 'monospace', fontSize: 8, color: i <= currentLayer ? '#2d8a87' : 'rgba(212,197,169,0.3)', letterSpacing: '.04em', textTransform: 'uppercase' }}>{layer.label}</span>
                  </div>
                ))}
              </div>
            </div>
            {fissionResults.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {fissionResults.map((result, i) => (
                  <div key={i} style={{ padding: '10px 12px', background: 'rgba(45,138,135,0.04)', border: '1px solid rgba(45,138,135,0.15)', borderRadius: 6 }}>
                    <p style={{ fontFamily: 'monospace', fontSize: 8, color: '#2d8a87', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 4 }}>{result.layer} · {result.arabic}</p>
                    <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 10, lineHeight: 1.6 }}>{result.content}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* RESULTS STATE */}
        {mode === 'results' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
              {imagePreview && (
                <div style={{ borderRadius: 8, overflow: 'hidden' }}>
                  <img src={imagePreview} alt="IRL Lens fission analysis result" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }} />
                </div>
              )}
              <div>
                <p style={{ fontFamily: 'monospace', fontSize: 9, color: '#2d8a87', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 8 }}>◎ Fission Complete</p>
                <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 10, lineHeight: 1.6 }}>
                  {fissionResults.length} layers analyzed · Linked to IRL Lens framework
                </p>
              </div>
            </div>

            {/* Fission layers */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {fissionResults.map((result, i) => (
                <div key={i} style={{ padding: '10px 14px', background: 'rgba(45,138,135,0.04)', border: '1px solid rgba(45,138,135,0.15)', borderRadius: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <p style={{ fontFamily: 'monospace', fontSize: 9, color: '#2d8a87', letterSpacing: '.08em', textTransform: 'uppercase', fontWeight: 700 }}>{result.layer}</p>
                    <p style={{ fontFamily: "'Amiri', serif", fontSize: 12, color: 'rgba(45,138,135,0.6)' }}>{result.arabic}</p>
                    {result.namesLink && <Link href="/names-reference" style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(138,138,184,0.6)', textDecoration: 'none', padding: '2px 6px', border: '1px solid rgba(138,138,184,0.2)', borderRadius: 4 }}>Names →</Link>}
                    {result.mrgLink && <Link href="/muslim-resource-guide" style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(45,122,71,0.6)', textDecoration: 'none', padding: '2px 6px', border: '1px solid rgba(45,122,71,0.2)', borderRadius: 4 }}>MRG →</Link>}
                  </div>
                  <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 11, lineHeight: 1.7 }}>{result.content}</p>
                </div>
              ))}
            </div>

            {/* MRG context */}
            <div style={{ padding: '12px 14px', background: 'rgba(45,122,71,0.06)', border: '1px solid rgba(45,122,71,0.2)', borderRadius: 8 }}>
              <p style={{ fontFamily: 'monospace', fontSize: 8, color: '#2d7a47', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 6 }}>⬡ MRG Context Card</p>
              <textarea
                value={mrgContext}
                onChange={e => setMrgContext(e.target.value)}
                placeholder="Add MRG context — what community resources or disparities does this observation connect to?"
                rows={2}
                style={{ width: '100%', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(45,122,71,0.2)', borderRadius: 6, padding: '8px 10px', color: '#F5F0E8', fontSize: 10, fontFamily: 'monospace', outline: 'none', resize: 'none', boxSizing: 'border-box' }}
              />
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button onClick={saveObservation} style={{ flex: 1, padding: '10px', background: 'rgba(45,122,71,0.12)', border: '1px solid rgba(45,122,71,0.4)', borderRadius: 8, color: '#2d7a47', fontFamily: 'monospace', fontSize: 9, letterSpacing: '.08em', textTransform: 'uppercase', cursor: 'pointer', transition: 'all 0.2s' }}>
                ◎ Save to Observation Chain
              </button>
              <Link href="/mrg-embed/collection" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px', background: 'rgba(45,138,135,0.08)', border: '1px solid rgba(45,138,135,0.3)', borderRadius: 8, color: '#2d8a87', fontFamily: 'monospace', fontSize: 9, letterSpacing: '.08em', textTransform: 'uppercase', textDecoration: 'none', transition: 'all 0.2s' }}>
                ⬡ Submit to MRG
              </Link>
              <button onClick={reset} style={{ padding: '10px 14px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, color: 'rgba(212,197,169,0.4)', fontFamily: 'monospace', fontSize: 9, letterSpacing: '.08em', textTransform: 'uppercase', cursor: 'pointer' }}>
                New Photo
              </button>
            </div>
          </div>
        )}

        {/* Saved observations */}
        {observations.length > 0 && mode !== 'camera' && mode !== 'analyzing' && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.3)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 10 }}>
              Observation Chain · {observations.length} saved
            </p>
            {observations.slice(0, 3).map(obs => (
              <div key={obs.id} style={{ display: 'flex', gap: 10, padding: '8px 10px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 6, marginBottom: 6 }}>
                <img src={obs.imagePreview} alt="Saved observation thumbnail" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.4)', marginBottom: 2 }}>{new Date(obs.timestamp).toLocaleString()}</p>
                  <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 9, lineHeight: 1.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {obs.fissionLayers[0]?.content?.slice(0, 80)}...
                  </p>
                </div>
              </div>
            ))}
            <Link href="/observation-chain-archive" style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(45,138,135,0.6)', textDecoration: 'none' }}>
              Full Archive →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
